import { useEffect, useRef } from 'react';
import APIAmplify from '@aws-amplify/api';

import localStorageUtil from '@/common/utils/localStorageUtil';
import S3PathUtils from '@/common/utils/S3PathUtil';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';

import { getTicketTelephoneStatusDetail } from '@/service/formManagementService'
import { onTicketUpdated } from '@/graphql/subscriptions';
import { buildWorkflowStatusData } from '@/component/chatThread/handler/DetailTicketHandler';
import { handleTriggerReloadPanelList } from '@/component/telephoneAgent/handler/TelephoneTicketPanelHandler';

/**
 * Custom hook to listen for ticket updates via GraphQL subscription
 * and update the corresponding field data in the panel without affecting user-editing state.
 */
const useTicketSubscription = ({
    detailTicket,
    fieldListDefaultData,
    mapDataIdToId,
    telephoneStatusOfTicket,
    setTelephoneStatusOfTicket,
    setSubscriptionFieldData,
    reloadTicketListByPanelType,
    ticketListType,
}) => {

    // Use ref to always have the latest telephoneStatusOfTicket inside subscription callback
    // This avoids stale closure issue since telephoneStatusOfTicket is not in useEffect dependencies
    const telephoneStatusRef = useRef(telephoneStatusOfTicket);
    useEffect(() => {
        telephoneStatusRef.current = telephoneStatusOfTicket;
    }, [telephoneStatusOfTicket]);

    useEffect(() => {
        const ticketDetail = detailTicket;
        if (Utils.isEmptyObject(ticketDetail)) return;
        if (Utils.isEmptyArray(fieldListDefaultData)) return;
        if (Utils.isEmptyObject(mapDataIdToId)) return;

        // Filter list ticketType subscription
        const ticketType = ticketDetail.ticketType;
        if (![
            Constant.TICKET_TYPE.TELEPHONE
        ].includes(ticketType)) return;
        
        // Check is opening ticket panel detail:
        const tabId = ticketDetail?.id;
        const tabPrefix = Utils.getPrefixOfId(tabId);
        const { REQ, KNOWLEDGE, TASK } = Constant.TICKET_PREFIX;
        if (![REQ, KNOWLEDGE, TASK].includes(tabPrefix)) return;
        
        let subscription;
        
        if (ticketType === Constant.TICKET_TYPE.TELEPHONE) {
            subscription = onTicketTelephoneSubscription({
                ticketDetail,
                listTicketFieldData: fieldListDefaultData,
                mappingObjTicketIdToFieldId: mapDataIdToId,
                telephoneStatusRef,
                setTelephoneStatusOfTicket,
                setSubscriptionFieldData,
                reloadTicketListByPanelType,
                ticketListType,
            });
        }

        // other logic handle subscription ticket by ticketType ...

        // clean-up: open new tab, change tab, close tab, panel
        return () => {
            if (subscription) {
                subscription?.unsubscribe();
                setSubscriptionFieldData({});
            }
        }

    }, [detailTicket?.id, fieldListDefaultData, mapDataIdToId])
};

export default useTicketSubscription;

/**
 * Listen for ticket TELEPHONE update events and update the corresponding field data in the panel
 */
const onTicketTelephoneSubscription = ({
    ticketDetail,
    listTicketFieldData,
    mappingObjTicketIdToFieldId,
    telephoneStatusRef,
    setTelephoneStatusOfTicket,
    setSubscriptionFieldData,
    reloadTicketListByPanelType,
    ticketListType,
}) => {
    if (Utils.isEmptyObject(ticketDetail)) return;
    if (Utils.isEmptyArray(listTicketFieldData)) return;
    if (Utils.isEmptyObject(mappingObjTicketIdToFieldId)) return;

    // Init List TicketFields For Subscription
    listTicketFieldData = listTicketFieldData.map(field => {
        let fieldData = {
            id: field.id,
            sysField: field?.sysField,
            value: field?.value
        };

        const ticketId = mappingObjTicketIdToFieldId[field.id];
        if (ticketId) {
            fieldData.ticketId = ticketId
        }

        return fieldData;
    });

    const listSubscriptionTicketField = listTicketFieldData.filter(field =>
        TelephoneConstant.LIST_TELEPHONE_FIELD_ON_SUBSCRIPTION_UPDATE.includes(field?.sysField)
    );

    const mainTicketId = ticketDetail.id;

    // Handle Subscription
    let subscription = APIAmplify.graphql({
        query: onTicketUpdated
    }).subscribe({
        next: async resData => {
            const updatedTicket = resData?.value?.data?.onTicketUpdated;
            if (Utils.isEmptyObject(updatedTicket)) return;
            
            const updatedTicketId = updatedTicket?.id;

            const listSubscriptionTicketId = listSubscriptionTicketField
                .map(ticket => ticket.ticketId)
                .filter(ticketId => !!ticketId);

            if (updatedTicketId === mainTicketId) {

                // Check & Update Telephone Status
                // Use ref to get latest value, avoiding stale closure
                const currentStatusId = telephoneStatusRef?.current?.currentItemData?.id;

                const updatedStatus = Utils.convertJSONDataToObject(updatedTicket?.telephoneStatus);
                const updateStatusId = updatedStatus?.status;

                if (
                    Utils.getPrefixOfId(updateStatusId) === Constant.ID_PREFIX.STATUS &&
                    currentStatusId !== updateStatusId
                ) {
                    const projectInuseId = localStorageUtil.getSelectedProjectId();
                    const userId = localStorageUtil.getUserDetailId();
                    const isHelpdeskSide = projectInuseId === ticketDetail.affiliationProject;
                    const ticket = {
                        telephoneStatus: updatedStatus,
                        affiliationOrganization: ticketDetail.affiliationOrganization,
                        metaData: ticketDetail.metaData,
                        customer: ticketDetail.customer,
                    }
                    const updateStatusDetail = await getTicketTelephoneStatusDetail(ticket, userId, isHelpdeskSide);
                    const telephoneWorkflowStatus = buildWorkflowStatusData(
                        ticketDetail,
                        {id: projectInuseId},
                        updatedStatus,
                        updateStatusDetail
                    );

                    setSubscriptionFieldData(prev => ({
                        ...prev,
                        telephoneStatus: telephoneWorkflowStatus?.currentItemData
                    }));
                    setTelephoneStatusOfTicket({ ...telephoneWorkflowStatus });
                    handleTriggerReloadPanelList(ticketListType, ticketDetail, reloadTicketListByPanelType);
                }
            }

            if (listSubscriptionTicketId.includes(updatedTicketId)) {
                for (let subscriptionField of listSubscriptionTicketField) {
                    const subsTicketId = subscriptionField.ticketId;
                    if (subsTicketId === updatedTicketId) {
                        let updatedValue = Utils.convertJSONDataToObject(updatedTicket?.dataValue)?.value;
                        if (!Utils.isEmptyString(updatedValue) && subscriptionField?.sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.TRANSCRIPT_URL) {
                            // define router access transcript_log
                            updatedValue = S3PathUtils.getPublicTelephoneTranscriptUrl(updatedValue, ticketDetail.id)
                        }
                        if (!Utils.isEmptyString(updatedValue) && subscriptionField?.sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_RECORDING_URL) {
                            // define router access call_recording_audio
                            updatedValue = S3PathUtils.getPublicTelephoneRecordingAudioUrl(ticketDetail.id)
                        }

                        setSubscriptionFieldData(prev => ({
                            ...prev,
                            [subscriptionField.id]: updatedValue
                        }));
                    }
                }
            }
        },
        error: err => {
            console.log('onSubscriptionTicketByTicketType >>> err >>>', err)
        }
    });

    return subscription;
};
